import {
    List,
    Datagrid,
    TextField,
    DateField,
    FunctionField,
    TopToolbar,
    CreateButton,
    FilterButton,
    TextInput,
    SelectInput,
} from 'react-admin';
import { Typography } from '@mui/material';
import { StatusChip } from './StatusChip';
import { FleetProgress } from './FleetProgress';
import { RejectionAlerts } from './RejectionAlerts';
import { RevokeAuthorizationButton } from './RevokeAuthorizationButton';
import type { NodeClaimAuthorization } from './types';

/**
 * Armed-but-unclaimed is the operational queue — the cards in flight — so it is the default
 * view, not a filter the operator has to discover. Clearing the status filter shows history.
 */
const claimFilters = [
    <SelectInput
        key="status"
        source="status"
        label="Status"
        alwaysOn
        emptyText="All"
        choices={[
            { id: 'Armed', name: 'Armed — awaiting card' },
            { id: 'Claimed', name: 'Claimed' },
            { id: 'Expired', name: 'Expired' },
            { id: 'Revoked', name: 'Revoked' },
        ]}
    />,
    <TextInput key="nodeId" source="nodeId" label="Node ID" />,
    <TextInput key="hardwareSerial" source="hardwareSerial" label="Hardware serial" />,
];

const ListActions = () => (
    <TopToolbar>
        <FilterButton />
        <CreateButton label="Arm a node" />
    </TopToolbar>
);

export const NodeClaimList = () => (
    <>
        {/* Alerts first: a node being refused is the thing worth interrupting for. #1248 */}
        <RejectionAlerts />
        <FleetProgress />
        <List
            filters={claimFilters}
            actions={<ListActions />}
            filterDefaultValues={{ status: 'Armed' }}
            sort={{ field: 'armedAt', order: 'DESC' }}
            empty={false}
        >
            <Datagrid bulkActionButtons={false}>
                <TextField source="hardwareSerial" label="Hardware serial" />
                <TextField source="nodeId" label="Node" />
                <FunctionField
                    label="Status"
                    source="status"
                    render={(record: NodeClaimAuthorization) => <StatusChip status={record.status} />}
                />
                <DateField source="armedAt" label="Armed" showTime />
                <DateField source="expiresAt" label="Expires" showTime />
                <TextField source="armedBy" label="Armed by" />
                <DateField source="claimedAt" label="Claimed" showTime emptyText="—" />
                {/* A real control, not decoration: at 20-50 nodes an operator can check this
                    against where the card was actually posted. */}
                <FunctionField
                    label="Claimed from IP"
                    source="claimedFromIp"
                    render={(record: NodeClaimAuthorization) =>
                        record.claimedFromIp
                            ? <Typography variant="body2" fontFamily="monospace">{record.claimedFromIp}</Typography>
                            : <>—</>
                    }
                />
                <TextField source="reason" label="Reason" />
                <FunctionField
                    label=""
                    render={(record: NodeClaimAuthorization) => <RevokeAuthorizationButton record={record} />}
                />
            </Datagrid>
        </List>
    </>
);
